import dayjs from "dayjs";
import {
  CartSnapshot,
  DecisionSnapshot,
  ResponseDecisions,
  SnapshotsGroupedByTrackId,
  TrackID,
  UserDecisionPublic,
} from "@/shared/types";
import { ObservableObject, batch, observable } from "@legendapp/state";
import { CartBufferManager } from "./carts-buffer-manager";
import { convertToUtc } from "../utils";
import { getTimezone } from "@/shared";
import { insightsApiInstance } from "@/shared/api/axios";

// It manages fetching and buffering of the data

type TimeRange = {
  start: Date | null;
  end: Date | null;
};

type Config = {
  startOffset: number;
  chunkSize: number;
  pollingInterval: number;
  retryInterval: number;
  isLive: boolean;
  timezone: string;
  trackIds: TrackID[];
};

type FetchSegmentFnParams = {
  start?: Date | null;
  end: Date;
  page?: number;
  limit?: number;
  trackIds: TrackID[];
};

const DEFAULT_CONFIG: Config = {
  chunkSize: 5000,
  startOffset: 0,
  pollingInterval: 2000,
  retryInterval: 1000,
  isLive: false,
  timezone: getTimezone(),
  trackIds: [],
};

export class CartBufferClient {
  public timeRange: ObservableObject<TimeRange>;
  public page = 1;
  public pagesCount = 0;
  public limit = 50; // 20 items per page
  public total = 0;

  private bufferManager: CartBufferManager;
  private config: Config = DEFAULT_CONFIG;

  static MAX_BUFFER_IN_ONE_FETCH = 1000 * 60;

  constructor() {
    const bufferSize = 1000 * 60 * 10; // 10 minutes
    this.bufferManager = new CartBufferManager(bufferSize);
    this.timeRange = observable<TimeRange>({
      start: null,
      end: null,
    });
  }

  async init(start: Date, end: Date, config?: Partial<Config>) {
    this.config = { ...this.config, ...config };

    batch(() => {
      this.timeRange.end.set(end);
      this.bufferManager.setStartTime(start);
    });
  }

  destroy() {
    batch(() => {
      this.timeRange.start.set(null);
      this.timeRange.end.set(null);
    });
    this.bufferManager.clear();
  }

  async fetchSegmentObjects(params: FetchSegmentFnParams) {
    return (
      await insightsApiInstance.get<ResponseDecisions>("/cart/decisions/", {
        params: {
          ...(params.start && {
            from_datetime: convertToUtc(params.start),
          }),
          to_datetime: convertToUtc(params.end),
          page: params.page || this.page,
          limit: params.limit || this.limit,
          track_ids: params.trackIds.toString(),
        },
      })
    ).data;
  }

  async loadLiveSegment(toDateTime: Date, trackIds: TrackID[]) {
    const { results, number_of_pages } = await this.fetchSegmentObjects({
      end: toDateTime,
      trackIds,
    });

    if (results.length === 0) {
      // this.stopBuffer();
      // return;
    }

    if (results.length > 0) {
      const cartSnapshots = transformDecisionsIntoSnapshots(results);
      this.bufferManager.addObjects(cartSnapshots);
    }

    if (number_of_pages > 1) {
      const otherPages = await Promise.all(
        Array(number_of_pages - 1).map((_, index) =>
          this.fetchSegmentObjects({
            end: toDateTime,
            page: index + 2,
            trackIds,
          })
        )
      );

      otherPages.map((pageData) => {
        const cartSnapshots = transformDecisionsIntoSnapshots(pageData.results);
        this.bufferManager.addObjects(cartSnapshots);
      });
    }
  }

  getTracks() {
    return this.bufferManager.getKeys();
  }

  getSnapshotToDraw(key: string, datetime: number): CartSnapshot | null {
    return this.bufferManager.getSnapshotToDraw(key, datetime);
  }
}

/**
 * Groups decisions by track ID
 */
function normalizeDecisionsByTrackId(decisions: UserDecisionPublic[]) {
  const normalizedData: Record<string, UserDecisionPublic[]> = {};

  for (const decision of decisions) {
    if (decision.track_id in normalizedData) {
      normalizedData[decision.track_id].push(decision);
    } else {
      normalizedData[decision.track_id] = [decision];
    }
  }

  return normalizedData;
}
/**
 * 1. Group decisions by track ID
 * 2. Sort them ascending by time
 * 3. Calculate accumulated quantity for each track
 * @returns snapshots grouped by track ID
 */
const transformDecisionsIntoSnapshots = (
  results: UserDecisionPublic[]
): SnapshotsGroupedByTrackId<DecisionSnapshot>[] => {
  const normalizedDecisions = normalizeDecisionsByTrackId(results);
  const tracksSnapshots: SnapshotsGroupedByTrackId<DecisionSnapshot>[] = [];

  Object.keys(normalizedDecisions).forEach((trackId) => {
    // Group decisions by track ID
    const trackDecisions = normalizedDecisions[Number(trackId)];

    // Sort decisions ascending by time
    const sortedDecisions = trackDecisions.sort((a, b) => {
      return dayjs(a.time).unix() - dayjs(b.time).unix();
    });

    const snapshots = [];

    // Calculate accumulated quantity for each track
    for (let i = 0; i < sortedDecisions.length; i++) {
      const decision: DecisionSnapshot = {
        ...sortedDecisions[i],
        totalQuantity: 0,
        datetime: sortedDecisions[i].time,
      };

      if (i == 0) {
        decision.totalQuantity = decision.quantity;
      } else {
        const prevDecision = sortedDecisions[i - 1];
        const delta = decision.quantity + prevDecision.quantity;
        decision.totalQuantity = delta;
      }

      decision.datetime = decision.time;
      snapshots.push(decision);
    }

    tracksSnapshots.push({
      track_id: trackId,
      snapshots,
    });
  });

  return tracksSnapshots;
};
